import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import SpendTable from "./SpendTable";
import { Line } from "react-chartjs-2";
import React from "react";
import { useSelector } from "react-redux";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const ChartDashboard = ({ filter }) => {
  const {
    monthlyTransactions,
    lastThreeMonthsTransactions,
    yearlyTransactions,
  } = useSelector((state) => state.engagementMetrics);

  const formatShortNumber = (num) => {
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num;
  };

  const transactionData =
    filter === "Yearly"
      ? yearlyTransactions
      : filter === "Last 3 Months"
      ? lastThreeMonthsTransactions
      : monthlyTransactions;

  const labels = transactionData.map((item) =>
    filter === "Yearly" ? `${item.year}` : `${item.month}`
  );

  const actualSpending = transactionData.map(
    (item) => parseFloat(item.amount) || 0
  );
  const budgetSpending = transactionData.map(
    (item) => parseFloat(item.budget) || 0
  );

  const minActual = Math.min(...actualSpending);
  const minBudget = Math.min(...budgetSpending);
  const minYValue =
    Math.floor(Math.min(minActual, minBudget) / 1000) * 1000 || 0;

  const options = {
    responsive: true,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (context) => `$${context.parsed.y.toLocaleString()}`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: false,
        min: minYValue,
        ticks: {
          callback: (value) => `${formatShortNumber(value)}`,
        },
      },
    },
  };

  const totalSpendData = {
    labels,
    datasets: [
      {
        label: "Spending",
        data: actualSpending,
        borderColor: "#3b82f6",
        borderWidth: 2,
        fill: false,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 0,
      },
    ],
  };

  const trendAnalysisData = {
    labels,
    datasets: [
      {
        label: "Budget Data",
        data: budgetSpending,
        borderColor: "#0088FF",
        backgroundColor: "rgba(59, 130, 246, 0.2)",
        borderWidth: 1,
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 10,
      },
      {
        label: "Actual Spending",
        data: actualSpending,
        borderColor: "#EAF524",
        backgroundColor: "rgba(234, 245, 36, 0.10)",
        borderWidth: 1,
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 10,
      },
    ],
  };

  return (
    <div className="space-y-8">
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex justify-between mb-4">
          <h2 className="text-lg font-semibold">Total Spend</h2>
        </div>
        <Line data={totalSpendData} options={options} />
      </div>

      <SpendTable filter={filter} />

      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex justify-between mb-4">
          <h2 className="text-lg font-semibold">Spend trend analysis</h2>
        </div>

        <Line data={trendAnalysisData} options={options} />

        <div className="text-sm mt-2 ml-1">
          <div className="flex flex-col gap-2">
            <div className="flex items-center space-x-2">
              <span className="inline-block w-6 h-6 bg-blue-500"></span>
              <span>Budget Data</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="inline-block w-6 h-6 bg-[#EAF524]"></span>
              <span>Spend Data</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartDashboard;
